import React, { useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FeatureTrialNotice, FeatureUnavailableBanner, PageBody, PageTitle, PlanUpgradeBanner } from 'src/components';
import { useFeatureAvailability } from 'src/hooks/useFeatureAvailability';
import { useFeatureTrialsQuery } from 'src/api/queries/subscriptionQueries';
import { useDeliveryInsightsEnabled } from 'src/hooks/useDeliveryInsightsEnabled';
import { DeliveryAnalyticsFilters } from 'src/types/TDeliveryAnalytics';
import CampaignFilterBar from './components/CampaignFilterBar';
import { useDeliveryFilters } from './useDeliveryFilters';
import PopularAssetsTab from './tabs/PopularAssetsTab';
import AudienceTab from './tabs/AudienceTab';
import CachePerformanceTab from './tabs/CachePerformanceTab';
import TransformationsTab from './tabs/TransformationsTab';
import CampaignsTab from './tabs/CampaignsTab';
import ChannelsTab from './tabs/ChannelsTab';
import TopDomainsTab from './tabs/TopDomainsTab';
import styles from './styles.module.scss';

type TabKey =
  | 'assets'
  | 'audience'
  | 'cache'
  | 'transforms'
  | 'campaigns'
  | 'channels'
  | 'domains';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'assets', label: 'Popular assets' },
  { key: 'audience', label: 'Audience' },
  // 'Cache performance' tab intentionally hidden.
  { key: 'transforms', label: 'Transformations' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'channels', label: 'Channels' },
  { key: 'domains', label: 'Top delivery domains' },
];

function isTabKey(v: string | null): v is TabKey {
  return TABS.some((t) => t.key === v);
}

const renderTab = (tab: TabKey, filters: DeliveryAnalyticsFilters) => {
  switch (tab) {
    case 'audience':
      return <AudienceTab filters={filters} />;
    case 'cache':
      return <CachePerformanceTab filters={filters} />;
    case 'transforms':
      return <TransformationsTab filters={filters} />;
    case 'campaigns':
      return <CampaignsTab filters={filters} />;
    case 'channels':
      return <ChannelsTab filters={filters} />;
    case 'domains':
      return <TopDomainsTab filters={filters} />;
    case 'assets':
    default:
      return <PopularAssetsTab filters={filters} />;
  }
};

const DeliveryAnalytics: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { range, attrs, queryFilters, setRange, setAttrs, clearAttrs } = useDeliveryFilters();
  const probeUnavailable = useFeatureAvailability('delivery-analytics') === 'unavailable';
  const deliveryAddonEnabled = useDeliveryInsightsEnabled();
  // The addons blob doesn't know about feature trials — the server's
  // entitlement seam (`is_addon_enabled`) falls through to active trials,
  // so a mid-trial account is served by the API while the blob still says
  // no. Ask the trials endpoint (only when the blob says no) both for an
  // ACTIVE trial (→ show the dashboards) and for trial AVAILABILITY
  // (→ show the CTA on the gate banner). Server-emitted 403 gates carry
  // the same answers in their payload; this page blocks before any 403.
  const { data: featureTrials } = useFeatureTrialsQuery({
    enabled: !deliveryAddonEnabled,
  });
  const deliveryTrialActive = Boolean(
    featureTrials?.trials?.some(
      (t) => t.feature_code === 'DELIVERY_INSIGHTS' && t.active,
    ),
  );
  const deliveryEntitled = deliveryAddonEnabled || deliveryTrialActive;
  const unavailable = !deliveryEntitled || probeUnavailable;
  const deliveryTrialAvailable = Boolean(
    featureTrials?.trials_enabled &&
      featureTrials?.available_features?.includes('DELIVERY_INSIGHTS'),
  );

  const tabParam = useMemo(
    () => new URLSearchParams(location.search).get('tab'),
    [location.search],
  );
  const activeTab: TabKey = isTabKey(tabParam) ? tabParam : 'assets';

  const setTab = (tab: TabKey) => {
    const next = new URLSearchParams(location.search);
    next.set('tab', tab);
    history.replace({ pathname: location.pathname, search: next.toString() });
  };

  // Suggestions are scoped to the selected range only (not the attr filters)
  // so narrowing one dimension doesn't empty the others' autocomplete.
  const rangeOnlyFilters: DeliveryAnalyticsFilters = {
    from: queryFilters.from,
    to: queryFilters.to,
  };

  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Delivery Insights" className={styles.pageTitle} />
      <PageBody>
        {/* Two distinct "can't use this" states, two banners:
            - Addon not on the plan → the standard plan-upgrade banner, which
              owns the copy and the CTA: it names the required tier while that
              tier is on sale, and switches to the Enterprise conversation when
              it is not (see PlanUpgradeBanner). Plus the 14-day trial CTA when
              the trials endpoint says this account can still start one.
            - Backend API missing on the old release (probe) → the generic
              not-enabled banner. A transient receiver outage instead
              surfaces as a per-tab 502/503 message (the feature stays
              "available"/"unknown" here). */}
        {/* Mid-trial: say so — the dashboards below just work, and without
            this line the access looks permanent until the expiry email. */}
        <FeatureTrialNotice featureCode="DELIVERY_INSIGHTS" label="Delivery Insights" />
        {!deliveryEntitled ? (
          <PlanUpgradeBanner
            gate={{
              feature: 'delivery_insights',
              requiredPlan: 'STARTER',
              addonCode: 'DELIVERY_INSIGHTS',
              trialAvailable: deliveryTrialAvailable,
              trialDays: featureTrials?.trial_days,
            }}
          />
        ) : (
          probeUnavailable && <FeatureUnavailableBanner />
        )}

        <p className={styles.overview}>
          See how your delivered images and videos perform across the CDN — which
          assets are requested most, where your audience is, the transformations
          and campaigns driving traffic, and the domains serving your content. Use
          the time range and campaign filters to focus on a specific period,
          channel, or audience.
        </p>

        <CampaignFilterBar
          range={range}
          attrs={attrs}
          rangeFilters={rangeOnlyFilters}
          onRangeChange={setRange}
          onApply={setAttrs}
          onClear={clearAttrs}
          disabled={unavailable}
        />

        <p className={styles.infoNote}>
          These views show the top 100 results for the selected range and aren’t
          paginated. Narrow the time range or use the campaign filters to focus on a
          subset.
        </p>

        <p className={styles.infoNote}>
          Want this data in GA4 or Segment?{' '}
          <Link to="/insights/destinations">Set up Analytics Integrations</Link>.
        </p>

        <div className={styles.tabStrip} role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={activeTab === t.key}
              className={`${styles.tab} ${activeTab === t.key ? styles.tabActive : ''}`}
              onClick={() => setTab(t.key)}
              disabled={unavailable}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.tabPanel}>
          {unavailable ? (
            <div className={styles.emptyState}>
              Delivery insights will appear here once the feature is enabled for
              your account.
            </div>
          ) : (
            renderTab(activeTab, queryFilters)
          )}
        </div>
      </PageBody>
    </div>
  );
};

export default DeliveryAnalytics;
